import React, { useEffect, useMemo, useRef, useState } from 'react';

import { ConversationStatus } from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import Column from '@amzn/meridian/column';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Toggle from '@amzn/meridian/toggle';

import { isNil } from 'lodash';
import { Message, MessageGroup, TypingIndicator } from 'src/components/AI';
import { AIChatInput } from 'src/components/AI/AIChatInput';
import { UploadDocumentButton } from 'src/components/AI/DocumentAnalysis';
import { groupMessagesIntoInteractions } from 'src/components/AI/utils';
import { EmbeddedScrollable } from 'src/components/EmbeddedScrollable';

interface IAIChatSession {
    conversationId: string;
    conversationStatus: ConversationStatus;
    messages: Message[];
    handleSendMessage: (message: string) => Promise<void>;
    isSending: boolean;
    isDisabled: boolean;
    isAIResponding: boolean;
    isLoading: boolean;
}

export const AIChatSession: React.FC<IAIChatSession> = (props) => {
    const { conversationId, conversationStatus, messages, handleSendMessage } = props;
    const { isSending, isDisabled, isAIResponding, isLoading } = props;

    const [showTechnicalMessages, setShowTechnicalMessages] = useState(false);
    const uploadButtonRef = useRef<HTMLDivElement>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const handleAttachFile = () => {
        const fileInput = uploadButtonRef.current?.querySelector('input[type="file"]') as HTMLInputElement;
        fileInput?.click();
    };

    // Filter messages based on tool message toggle state
    const filteredMessages = useMemo(() => {
        if (showTechnicalMessages) {
            return messages; // Show all messages when toggle is on
        }
        // Hide tool messages when toggle is off
        return messages.filter((message) => message.messageType !== 'tool');
    }, [messages, showTechnicalMessages]);

    // Group messages into interactions for collapsible display
    const messageInteractions = useMemo(
        () => groupMessagesIntoInteractions(filteredMessages, conversationStatus),
        [filteredMessages, conversationStatus]
    );

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    return (
        <Column width="100%" height={'100%'} heights={['fill', 'fit']}>
            <EmbeddedScrollable>
                {isLoading && messages.length === 0 ? (
                    <Column alignmentVertical="center" alignmentHorizontal="center" height="100%">
                        <Loader size="medium" />
                        <Text>Loading chat...</Text>
                    </Column>
                ) : messages.length === 0 ? (
                    <Column alignmentVertical="center" alignmentHorizontal="center" height="100%">
                        <Text>No messages yet. Start the chat!</Text>
                    </Column>
                ) : (
                    <Column>
                        {messageInteractions.map((interaction, index) => (
                            <MessageGroup
                                key={`interaction-${index}-${interaction.userMessage.timestamp}`}
                                interaction={interaction}
                                conversationId={conversationId || undefined}
                                showTechnicalMessages={showTechnicalMessages}
                            />
                        ))}
                        {isAIResponding && <TypingIndicator />}
                        <div ref={messagesEndRef} />
                    </Column>
                )}
            </EmbeddedScrollable>

            <Column spacing="200">
                <AIChatInput
                    conversationStatus={conversationStatus}
                    handleSendMessage={handleSendMessage}
                    isSending={isSending}
                    isDisabled={isDisabled}
                    conversationId={conversationId}
                    showTechnicalMessages={showTechnicalMessages}
                    onToggleTechnicalMessages={setShowTechnicalMessages}
                    onAttachFile={handleAttachFile}
                />
                <div ref={uploadButtonRef} style={{ display: 'none' }}>
                    <UploadDocumentButton conversationId={conversationId} />
                </div>
            </Column>
        </Column>
    );
};
